import { InputHTMLAttributes, SelectHTMLAttributes, ReactNode } from "react";

// Form primitives that handle the floating-label / consistent border styling
// once, so pages don't have to repeat 8 className utilities. Label sits
// above the field, optional hint/error renders underneath.

type FieldProps = {
  label?: string;
  hint?: string;
  error?: string;
  children: ReactNode;
};

function Field({ label, hint, error, children }: FieldProps) {
  return (
    <label className="block">
      {label && (
        <span className="block text-[11px] uppercase tracking-wide text-sub mb-1">
          {label}
        </span>
      )}
      {children}
      {error ? (
        <span className="block text-[11px] text-state-failed mt-1">{error}</span>
      ) : hint ? (
        <span className="block text-[11px] text-sub mt-1">{hint}</span>
      ) : null}
    </label>
  );
}

type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  label?: string;
  hint?: string;
  error?: string;
};

export function Input({ label, hint, error, className = "", ...rest }: InputProps) {
  return (
    <Field label={label} hint={hint} error={error}>
      <input
        className={`w-full bg-card border border-border rounded-md px-3 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary ${error ? "border-state-failed" : ""} ${className}`}
        {...rest}
      />
    </Field>
  );
}

type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  label?: string;
  hint?: string;
  error?: string;
  children: ReactNode;
};

export function Select({
  label,
  hint,
  error,
  className = "",
  children,
  ...rest
}: SelectProps) {
  return (
    <Field label={label} hint={hint} error={error}>
      <select
        className={`w-full bg-card border border-border rounded-md px-3 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary ${className}`}
        {...rest}
      >
        {children}
      </select>
    </Field>
  );
}
